import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Popper,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterTableView from "./ColumnFilterPopup";
import api from "../../services/api";
import Loader from "../Alerts/Loader";

// Custom Popper component to allow horizontal scrolling
const CustomPopper = (props) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ]}
      style={{
        ...props.style,
        width: "auto",
        minWidth: props.anchorEl?.clientWidth || "auto",
      }}
    />
  );
};

export default function ScheduleEmail() {
  const [openFilters, setOpenFilters] = useState(false);
  const [loading, setLoading] = useState(false);

  // State management
  const [databases, setDatabases] = useState([]);
  const [tables, setTables] = useState([]);
  const [columnsByTable, setColumnsByTable] = useState({});

  const [database, setDatabase] = useState("");
  const [baseTable, setBaseTable] = useState("");
  const [joins, setJoins] = useState([]);

  // Data from API
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);

  const navigate = useNavigate();

  // Common styles for Autocomplete Paper component
  const paperProps = {
    sx: {
      maxHeight: 200,
      overflowY: "auto",
      overflowX: "auto",
      "& .MuiAutocomplete-listbox": {
        maxHeight: "200px",
        overflowY: "auto",
        overflowX: "auto",
      },
      "& .MuiAutocomplete-option": {
        whiteSpace: "nowrap",
        overflow: "visible",
      },
    },
  };

  // Validation helper: Check if all joins are complete
  const areAllJoinsValid = () => {
    if (joins.length === 0) return true;

    return joins.every(
      (join) =>
        join.leftTable && join.leftColumn && join.rightTable && join.rightColumn
    );
  };

  // Fetch databases on component mount
  useEffect(() => {
    const fetchDatabases = async () => {
      setLoading(true);
      try {
        const response = await api.get("/schedule_email/get_databases");

        if (response.data.success) {
          setDatabases(response.data.databases);
        } else {
          console.error("Error fetching databases:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching databases:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDatabases();
  }, []);

  // Fetch tables when database changes
  useEffect(() => {
    if (!database) {
      setTables([]);
      return;
    }

    const fetchTables = async () => {
      setLoading(true);
      try {
        const response = await api.get("/schedule_email/get_tables", {
          params: { db_name: database },
        });

        if (response.data.success) {
          setTables(response.data.tables);
        } else {
          console.error("Error fetching tables:", response.data.message);
          setTables([]);
        }
      } catch (error) {
        console.error("Error fetching tables:", error);
        setTables([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTables();
  }, [database]);

  // Fetch columns for a specific table
  const fetchColumns = async (tableName) => {
    if (!database || !tableName) return;

    try {
      const response = await api.get("/schedule_email/get_columns", {
        params: {
          db_name: database,
          table_name: tableName,
        },
      });

      if (response.data.success) {
        setColumnsByTable((prev) => ({
          ...prev,
          [tableName]: response.data.columns,
        }));
      } else {
        console.error("Error fetching columns:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching columns:", error);
    }
  };

  // Fetch columns when base table changes
  useEffect(() => {
    if (baseTable) {
      fetchColumns(baseTable);
    }
  }, [baseTable]);

  const handleDatabaseChange = (value) => {
    setDatabase(value);
    setBaseTable("");
    setJoins([]);
    setColumnsByTable({});
    setData([]);
    setColumns([]);
  };

  const handleBaseTableChange = (value) => {
    setBaseTable(value);
    setJoins([]);
    setData([]);
    setColumns([]);
  };

  const addJoin = () => {
    setJoins([
      ...joins,
      {
        leftTable: baseTable,
        leftColumn: "",
        rightTable: "",
        rightColumn: "",
      },
    ]);
  };

  const removeJoin = (index) => {
    setJoins(joins.filter((_, i) => i !== index));
    setData([]);
    setColumns([]);
  };

  const updateJoin = (index, field, value) => {
    const updated = [...joins];
    updated[index][field] = value;

    if (field === "leftTable") {
      updated[index].leftColumn = "";
      fetchColumns(value);
    }
    if (field === "rightTable") {
      updated[index].rightColumn = "";
      fetchColumns(value);
    }

    setJoins(updated);
    setData([]);
    setColumns([]);
  };

  const handleVisualize = () => {
    if (!database || !baseTable) {
      alert("Please select a database and table first.");
      return;
    }

    if (joins.length > 0 && !areAllJoinsValid()) {
      alert("Please complete all join conditions before visualizing.");
      return;
    }

    setOpenFilters(true);
  };

  return (
    <div className="w-full p-6 space-y-6">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: "#2c3e50",
            "&:hover": {
              backgroundColor: "#e3f2fd",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h4" fontWeight="600" sx={{ color: "#2c3e50" }}>
          Data Filteration
        </Typography>
      </Box>
      <FilterTableView
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        dbName={database}
        baseTable={baseTable}
        joins={joins}
      />

      {/* STEP 1 — SELECT DATABASE */}
      <div className="grid grid-cols-2 gap-4">
        {/* LEFT SIDE: DATABASE SELECTION */}
        <div className="bg-blue-50 p-4 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">
            Select Database
          </h2>

          <Autocomplete
            value={database || null}
            onChange={(event, newValue) => {
              handleDatabaseChange(newValue || "");
            }}
            options={databases}
            PopperComponent={CustomPopper}
            componentsProps={{
              paper: paperProps,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Choose a database"
                size="small"
                className="bg-white rounded"
              />
            )}
            fullWidth
          />
        </div>

        {/* RIGHT SIDE: TABLE SELECTION */}
        {database && (
          <div className="bg-blue-50 p-4 rounded-xl shadow">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">
              Select Table
            </h2>

            <Autocomplete
              value={baseTable || null}
              onChange={(event, newValue) => {
                handleBaseTableChange(newValue || "");
              }}
              options={tables}
              PopperComponent={CustomPopper}
              componentsProps={{
                paper: paperProps,
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a table"
                  size="small"
                  className="bg-white rounded"
                />
              )}
              fullWidth
            />
          </div>
        )}
      </div>

      {/* JOINS UI */}
      {database && baseTable && (
        <>
          {joins.map((join, index) => (
            <div key={index}>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6 items-center">
                {/* LEFT CARD - TABLE SELECTION */}
                <div className="bg-blue-50 p-4 rounded-xl shadow space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold">Join Data</h2>

                    <div className="flex items-center gap-4">
                      <Autocomplete
                        value={join.leftTable || null}
                        onChange={(event, newValue) => {
                          updateJoin(index, "leftTable", newValue || "");
                        }}
                        options={tables}
                        PopperComponent={CustomPopper}
                        componentsProps={{
                          paper: paperProps,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Left Table"
                            size="small"
                            className="bg-white"
                          />
                        )}
                        sx={{ width: 150 }}
                      />

                      <AddLinkIcon className="text-gray-500" />

                      <Autocomplete
                        value={join.rightTable || null}
                        onChange={(event, newValue) => {
                          updateJoin(index, "rightTable", newValue || "");
                        }}
                        options={tables}
                        PopperComponent={CustomPopper}
                        componentsProps={{
                          paper: paperProps,
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Right Table"
                            size="small"
                            className="bg-white"
                          />
                        )}
                        sx={{ width: 150 }}
                      />
                    </div>

                    <IconButton onClick={() => removeJoin(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>

                {/* CENTER - "On" TEXT */}
                <div className="text-center font-semibold text-lg">On</div>

                {/* RIGHT CARD - COLUMN SELECTION */}
                <div className="bg-green-50 p-4 rounded-xl shadow space-y-4">
                  <div className="flex items-center gap-3">
                    <Autocomplete
                      value={join.leftColumn || null}
                      onChange={(event, newValue) => {
                        updateJoin(index, "leftColumn", newValue || "");
                      }}
                      options={columnsByTable[join.leftTable] || []}
                      disabled={!join.leftTable}
                      PopperComponent={CustomPopper}
                      componentsProps={{
                        paper: paperProps,
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Column"
                          size="small"
                          className="bg-white"
                        />
                      )}
                      sx={{ width: 180 }}
                    />

                    <span className="text-xl font-semibold">=</span>

                    <Autocomplete
                      value={join.rightColumn || null}
                      onChange={(event, newValue) => {
                        updateJoin(index, "rightColumn", newValue || "");
                      }}
                      options={columnsByTable[join.rightTable] || []}
                      disabled={!join.rightTable}
                      PopperComponent={CustomPopper}
                      componentsProps={{
                        paper: paperProps,
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Column"
                          size="small"
                          className="bg-white"
                        />
                      )}
                      sx={{ width: 180 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Show buttons only if all joins are valid */}
          {areAllJoinsValid() && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 space-y-4">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleVisualize}
                  sx={{
                    padding: "10px",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                  }}
                >
                  Visualize
                </Button>
              </div>
              <div className="p-4 space-y-4">
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={addJoin}
                  sx={{
                    padding: "10px",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                    },
                  }}
                >
                  + Add Join
                </Button>
              </div>
            </div>
          )}

          {/* Show message when joins are incomplete */}
          {!areAllJoinsValid() && joins.length > 0 && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
              <p className="text-yellow-800 font-medium">
                Please complete all join conditions to continue
              </p>
            </div>
          )}

          {loading && <Loader />}

          {/* DEBUG OUTPUT */}
          {/* <pre className="bg-gray-900 text-green-300 p-4 rounded-xl text-sm overflow-x-auto">
            {JSON.stringify(
              { database, baseTable, joins, allJoinsValid: areAllJoinsValid() },
              null,
              2
            )}
          </pre> */}
        </>
      )}
    </div>
  );
}
